/**
 * Integrations Store - Save/Load API configs from Firestore
 * Collection: integrations/{integrationName}
 * Only accessible by admins
 */

import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore'
import { db } from '../firebase'

const COLLECTION = 'integrations'

/**
 * Save integration config to Firestore
 */
export async function saveIntegrationConfig(name, config) {
  try {
    const docRef = doc(db, COLLECTION, name)
    await setDoc(docRef, {
      ...config,
      updatedAt: new Date().toISOString()
    }, { merge: true })
    return { success: true }
  } catch (error) {
    console.error(`Failed to save ${name} config:`, error)
    return { success: false, error: error.message }
  }
}

/**
 * Load integration config from Firestore
 */
export async function loadIntegrationConfig(name) {
  try {
    const docRef = doc(db, COLLECTION, name)
    const snapshot = await getDoc(docRef)

    if (snapshot.exists()) {
      return { success: true, config: snapshot.data() }
    }
    return { success: true, config: {} }
  } catch (error) {
    console.error(`Failed to load ${name} config:`, error)
    return { success: false, error: error.message, config: {} }
  }
}

/**
 * Subscribe to integration config changes (real-time)
 * @param {string} name - Integration name
 * @param {Function} callback - Called with config data on success or {} on error
 * @param {Function} [onError] - Optional error handler (e.g. toast.error)
 */
export function subscribeToIntegrationConfig(name, callback, onError) {
  const docRef = doc(db, COLLECTION, name)

  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data())
    } else {
      callback({})
    }
  }, (error) => {
    console.error(`Subscription error for ${name}:`, error)
    if (onError) onError(`Failed to load ${name} configuration`)
    callback({})
  })
}

/**
 * Load all integration configs at once
 */
export async function loadAllIntegrationConfigs(names) {
  const configs = {}

  await Promise.all(
    names.map(async (name) => {
      const result = await loadIntegrationConfig(name)
      configs[name] = result.config || {}
    })
  )

  return configs
}

/**
 * Check if an integration has required fields configured
 */
export function isIntegrationConfigured(config, requiredFields) {
  if (!config || !requiredFields) return false

  return requiredFields.every(field => {
    const value = config[field]
    return value && String(value).trim().length > 0
  })
}
